import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import '../styles/Login.css';
import { useAuth } from '../hooks/useAuth';
import AkrosLogo from '../components/AkrosLogo';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { login, loading, error } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    let isValid = true;
    
    // Email validation
    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Email is invalid');
      isValid = false;
    } else {
      setEmailError('');
    }
    
    // Password validation
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    } else {
      setPasswordError('');
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const success = await login(email, password);
      if (success) {
        navigate('/');
      }
    }
  };


  return (
    <Box sx={{
      bgcolor: 'var(--color-dark)',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 2,
    }}>
      <Paper elevation={3} className="auth-card" sx={{
        width: '100%',
        maxWidth: '480px',
        position: 'relative',
        pt: 0,
        px: 1,
        pb: 3
      }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 24 }}>
          <Box sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            mb: 3,
            px: 1
          }}>
            <Box
              sx={{
                width: '100%',
                minHeight: 176,
                bgcolor: 'var(--color-dark)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 4
              }}
            >
              <AkrosLogo
                iconColor="var(--color-primary)"
                nameColor="var(--color-white)"
                iconSize={72}
                height={54}
                gap={18}
                sx={{ width: '100%', justifyContent: 'center' }}
              />
            </Box>
          </Box>
          <Box sx={{
            width: '100%',
            p: 0,
            pl: 4
          }}>
            <Typography component="h2" variant="h6" fontWeight="700" sx={{
              fontSize: '1.2rem !important',
              pb: 3,
            }}>
              Sign In to Your Account
            </Typography>
          </Box>
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Box sx={{
            width: '100%',
            mb: 0,
            p: 0,
            px: 4
          }}>
            <Typography component="h2" variant="h6" fontWeight="600">
              Email*
            </Typography>
          </Box>
          
          <Box sx={{
            width: '100%',
            mb: 1,
            p: 0,
            px: 4
          }}>
            <TextField
              margin="dense"
              required
              fullWidth
              id="email"
              placeholder="Enter Email"
              name="email"
              autoComplete="off"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              disabled={loading}
              InputLabelProps={{ shrink: false }}
              sx={{
                pb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  height: '2rem',
                  padding: '0 12px',
                  '& input': {
                    padding: 0,
                    height: '2rem',
                    lineHeight: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& input::placeholder': {
                    lineHeight: '2rem',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1D1D1D'
                  },
                },
              }}
            />
          </Box>
          
          <Box sx={{
            width: '100%',
            mb: 0,
            p: 0,
            px: 4
          }}>
            <Typography component="h2" variant="h6" fontWeight="600">
              Password*
            </Typography>
          </Box>
          
          <Box sx={{
            width: '100%',
            mb: 1,
            p: 0,
            px: 4
          }}>
            <TextField
              margin="dense"
              required
              fullWidth
              name="password"
              placeholder="Enter Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              disabled={loading}
              InputLabelProps={{ shrink: false }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  height: '2rem',
                  padding: '0 12px',
                  '& input': {
                    padding: 0,
                    height: '2rem',
                    lineHeight: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& input::placeholder': {
                    lineHeight: '2rem',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1D1D1D'
                  },
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ p: 0 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 6 }}>
            <Button
              type="submit"
              variant="outlined"
              size="small"
              endIcon={
                <span style={{
                  fontSize: '1.2rem',
                  marginLeft: '0px',
                  color: 'inherit'
                }}>→</span>
              }
              className="login-button"
              disabled={loading}
              sx={{
                height: '40px',
                minHeight: '40px',
                '& .MuiButton-label': {
                  lineHeight: '40px'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default Login;
